import React from 'react';
import { Modal, Table, Tag, Typography, Button, Space, Tooltip } from 'antd';
import { ExtractedPanel, SourceCharacter, SourceCharacterCheckpoint } from '../../types';
import { CheckCircleOutlined, PushpinOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface DraftPanelsPreviewModalProps {
  visible: boolean;
  panels: ExtractedPanel[];
  characters: SourceCharacter[];
  checkpoints: SourceCharacterCheckpoint[];
  sceneTitle?: string;
  loading: boolean;
  onCancel: () => void;
  onSave: () => void;
}

const DraftPanelsPreviewModal: React.FC<DraftPanelsPreviewModalProps> = ({
  visible,
  panels,
  characters,
  checkpoints,
  sceneTitle,
  loading,
  onCancel,
  onSave,
}) => {
  const getCharacterName = (id: string) => {
    const char = characters.find(c => c.id === id);
    if (!char) return 'Unknown';
    const localizedName = (char.localizedData as any)?.name;
    return localizedName ? `${char.name} (${localizedName})` : char.name;
  };

  const getCheckpointTag = (panel: ExtractedPanel, charId: string) => {
    const cpMapping = panel.characterCheckpoints?.find(m => m.characterId === charId);
    if (!cpMapping) return null;

    const checkpoint = checkpoints.find(c => c.id === cpMapping.checkpointId);
    if (!checkpoint) return null;

    return (
      <Tooltip title={`Checkpoint: ${checkpoint.name} - ${checkpoint.description || 'Không có mô tả'}`}>
        <Tag color="orange" icon={<PushpinOutlined />} style={{ fontSize: '10px' }}>
          {checkpoint.name}
        </Tag>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Nội dung Panel (Biên kịch)',
      key: 'content',
      render: (_: any, record: ExtractedPanel) => (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong style={{ color: '#1890ff' }}>{record.title || 'Không có tiêu đề'}</Text>
          <Text>{record.caption}</Text>
        </Space>
      ),
    },
    {
      title: 'Nhân vật tham gia',
      key: 'characters',
      width: 300,
      render: (_: any, record: ExtractedPanel) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {record.characterIds && record.characterIds.length > 0 ? (
            record.characterIds.map((id, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Tag color="blue">{getCharacterName(id)}</Tag>
                {getCheckpointTag(record, id)}
              </div>
            ))
          ) : (
            <Text type="secondary" italic>Không có nhân vật</Text>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={`Bản thảo Panel (Biên kịch) - Scene: ${sceneTitle || 'Unknown'}`}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Hủy bỏ
        </Button>,
        <Button 
          key="save" 
          type="primary" 
          icon={<CheckCircleOutlined />} 
          onClick={onSave}
          loading={loading}
        >
          Lưu bản thảo ({panels.length} panels)
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          AI đã lên dàn ý các Panel cho phân cảnh này. Bước này chỉ tập trung vào nhịp kể chuyện (Tiêu đề, Lời thoại/Dẫn truyện) và nhân vật. Hình ảnh sẽ được đạo diễn ở bước sau.
        </Text>
      </div>
      <Table
        dataSource={panels}
        rowKey={(_, index) => index!.toString()}
        columns={columns}
        pagination={false}
        scroll={{ y: 500 }}
      />
    </Modal>
  );
};

export default DraftPanelsPreviewModal;